import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, CircularProgress, Box, TextField, InputAdornment, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
  Chip, Avatar, Tooltip, Divider, Slider, Stack, MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PercentIcon from '@mui/icons-material/Percent';
import SaveIcon from '@mui/icons-material/Save';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';
const fmt = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);
const CATEGORIES = ['Watches', 'Watch Spare Parts', 'Watch Accessories', 'Watch Box', 'Other'];

export default function AdminListingsPage() {
  const { authHeader } = useAuth();
  const [listings, setListings] = useState([]);
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [globalInput, setGlobalInput] = useState('0');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [onlyHidden, setOnlyHidden] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichMsg, setEnrichMsg] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editCategory, setEditCategory] = useState('');   // '' = automatica
  const [editVisible, setEditVisible] = useState('auto');  // 'auto' | 'show' | 'hide'
  const [editSaving, setEditSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/listings`, { headers: authHeader() });
      setListings(data.listings || []);
      setGlobalDiscount(data.globalDiscount || 0);
      setGlobalInput(String(data.globalDiscount || 0));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveGlobalDiscount = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/settings`, { global_discount_percent: globalInput }, { headers: authHeader() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const runEnrich = async () => {
    setEnriching(true);
    setEnrichMsg('Avvio…');
    let total = 0;
    try {
      for (let round = 0; round < 40; round++) { // limite di sicurezza (~5 min max)
        const { data } = await axios.post(`${API}/admin/enrich`, {}, { headers: authHeader() });
        total += data.enriched || 0;
        setEnrichMsg(`Categorizzati ${total} — ${data.remaining} da controllare…`);
        if (data.done || data.enriched === 0) break; // finito o niente più di recuperabile
      }
      setEnrichMsg(`Completato: ${total} articoli categorizzati.`);
      await load();
    } catch (e) {
      setEnrichMsg('Errore durante la ricategorizzazione.');
    } finally {
      setEnriching(false);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditPrice(item.customPrice != null ? String(item.customPrice) : '');
    setEditNotes(item.priceNotes || '');
    setEditCategory(item.categoryOverride || '');
    setEditVisible(item.visibleOverride === true ? 'show' : item.visibleOverride === false ? 'hide' : 'auto');
  };

  const saveEdit = async () => {
    if (!editItem) return;
    setEditSaving(true);
    try {
      await axios.patch(
        `${API}/admin/listings/${editItem.id}`,
        {
          custom_price: editPrice === '' ? null : parseFloat(editPrice),
          notes: editNotes,
          category: editCategory || null,
          visible: editVisible === 'show' ? true : editVisible === 'hide' ? false : null,
        },
        { headers: authHeader() }
      );
      setEditItem(null);
      await load();
    } catch (e) { console.error(e); }
    setEditSaving(false);
  };

  const removeOverride = async (item) => {
    await axios.patch(`${API}/admin/listings/${item.id}`, { custom_price: null }, { headers: authHeader() });
    await load();
  };

  const accountOf = (l) => l.account || 'account-1';
  const accounts = [...new Set(listings.map(accountOf))].sort();
  const accountLabel = (a) => `Account ${a.replace('account-', '')}`;

  const filtered = listings.filter(l =>
    (!search || l.title?.toLowerCase().includes(search.toLowerCase())) &&
    (accountFilter === 'all' || accountOf(l) === accountFilter) &&
    (!onlyHidden || l.visible === false)
  );

  const overrideCount = listings.filter(l => l.customPrice != null).length;
  const hiddenCount = listings.filter(l => l.visible === false).length;

  if (loading) return <Container sx={{ pt: 6, textAlign: 'center' }}><CircularProgress /></Container>;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Gestione Prezzi</Typography>

      {/* Global discount card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          <PercentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Sconto globale su tutto il catalogo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Applica una percentuale di sconto a tutti gli articoli che non hanno un prezzo personalizzato.
          Imposta 0 per disattivare.
        </Typography>

        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
          <Box sx={{ width: 300 }}>
            <Slider
              value={parseFloat(globalInput) || 0}
              onChange={(_, v) => setGlobalInput(String(v))}
              min={0} max={50} step={1}
              marks={[{ value: 0, label: '0%' }, { value: 25, label: '25%' }, { value: 50, label: '50%' }]}
              valueLabelDisplay="auto"
              valueLabelFormat={v => `${v}%`}
            />
          </Box>
          <TextField
            label="Percentuale %"
            value={globalInput}
            onChange={e => setGlobalInput(e.target.value)}
            type="number"
            size="small"
            sx={{ width: 130 }}
            inputProps={{ min: 0, max: 100, step: 1 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={saveGlobalDiscount}
            disabled={saving}
          >
            Salva sconto
          </Button>
          {saved && <Chip label="Salvato!" color="success" size="small" />}
        </Stack>

        {globalDiscount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Sconto attivo: <strong>−{globalDiscount}%</strong> su tutti gli articoli senza prezzo personalizzato
            ({listings.length - overrideCount} articoli).
            {overrideCount > 0 && ` ${overrideCount} articoli hanno un prezzo personalizzato.`}
          </Alert>
        )}
      </Paper>

      {/* Filtro per account (negozio) */}
      {accounts.length > 1 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Filtra per negozio eBay — utile per vedere cosa hanno rilevato gli account clienti
            (gli articoli "Nascosti" non compaiono sul sito perché senza categoria).
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Chip
              label={`Tutti (${listings.length})`}
              color={accountFilter === 'all' ? 'primary' : 'default'}
              onClick={() => setAccountFilter('all')}
              variant={accountFilter === 'all' ? 'filled' : 'outlined'}
            />
            {accounts.map(a => {
              const total = listings.filter(l => accountOf(l) === a).length;
              const hidden = listings.filter(l => accountOf(l) === a && l.visible === false).length;
              return (
                <Chip
                  key={a}
                  label={`${accountLabel(a)} · ${total}${hidden ? ` (${hidden} nascosti)` : ''}`}
                  color={accountFilter === a ? 'primary' : 'default'}
                  onClick={() => setAccountFilter(a)}
                  variant={accountFilter === a ? 'filled' : 'outlined'}
                />
              );
            })}
            <Box sx={{ flexGrow: 1 }} />
            <Chip
              label={`Solo nascosti (${hiddenCount})`}
              color={onlyHidden ? 'warning' : 'default'}
              onClick={() => setOnlyHidden(v => !v)}
              variant={onlyHidden ? 'filled' : 'outlined'}
            />
            <Button
              variant="contained"
              size="small"
              onClick={runEnrich}
              disabled={enriching}
              startIcon={enriching ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
            >
              Ricategorizza ora
            </Button>
          </Stack>
          {enrichMsg && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{enrichMsg}</Typography>
          )}
        </Paper>
      )}

      {/* Listings table */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField size="small" placeholder="Cerca articolo..." value={search}
          onChange={e => setSearch(e.target.value)} sx={{ width: 320 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
        <Typography variant="body2" color="text.secondary">
          {filtered.length} articoli
        </Typography>
      </Box>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell width={56}>Foto</TableCell>
              <TableCell>Titolo</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell align="right">Prezzo eBay</TableCell>
              <TableCell align="right">Prezzo visualizzato</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(l => {
              const hasOverride = l.customPrice != null;
              const hasDiscount = !hasOverride && globalDiscount > 0;
              return (
                <TableRow key={l.id} hover>
                  <TableCell>
                    <Avatar
                      src={l.image?.replace(/s-l\d+/, 's-l140') || ''}
                      variant="rounded"
                      sx={{ width: 44, height: 44 }}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap title={l.title}>{l.title}</Typography>
                    {l.priceNotes && (
                      <Typography variant="caption" color="text.secondary">{l.priceNotes}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={l.category} size="small"
                      variant={l.categoryOverride ? 'filled' : 'outlined'}
                      color={l.categoryOverride ? 'primary' : 'default'} />
                    {l.categoryOverride && (
                      <Tooltip title="Categoria impostata manualmente"><span> 🖉</span></Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{accountLabel(accountOf(l))}</Typography>
                  </TableCell>
                  <TableCell>
                    {l.visible === false
                      ? <Chip label="Nascosto" size="small" color="warning" />
                      : <Chip label="Visibile" size="small" color="success" variant="outlined" />}
                    {l.visibleOverride != null && (
                      <Tooltip title="Visibilità forzata manualmente"><span> 🖉</span></Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={hasDiscount || hasOverride ? 'text.disabled' : 'text.primary'}
                      sx={{ textDecoration: hasDiscount || hasOverride ? 'line-through' : 'none' }}>
                      {fmt(l.originalPrice)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold"
                      color={hasOverride ? 'primary.main' : hasDiscount ? 'success.main' : 'text.primary'}>
                      {fmt(l.displayPrice)}
                    </Typography>
                    {hasOverride && <Chip label="personalizzato" size="small" color="primary" sx={{ ml: 0.5 }} />}
                    {hasDiscount && <Chip label={`−${globalDiscount}%`} size="small" color="success" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifica prezzo">
                      <IconButton size="small" onClick={() => openEdit(l)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {hasOverride && (
                      <Tooltip title="Rimuovi prezzo personalizzato">
                        <IconButton size="small" color="error" onClick={() => removeOverride(l)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Edit dialog */}
      <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Modifica articolo — <Typography component="span" variant="body2" color="text.secondary" noWrap>{editItem?.title}</Typography></DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Prezzo originale eBay: <strong>{editItem ? fmt(editItem.originalPrice) : ''}</strong>
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <TextField
            label="Prezzo personalizzato (€)"
            value={editPrice}
            onChange={e => setEditPrice(e.target.value)}
            type="number"
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Lascia vuoto per usare il prezzo eBay con lo sconto globale"
            InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
          />
          <TextField
            label="Note interne (opzionale)"
            value={editNotes}
            onChange={e => setEditNotes(e.target.value)}
            fullWidth
            size="small"
            placeholder="es. Prezzo trattato con cliente"
          />

          <Divider sx={{ my: 2 }} />
          <TextField
            select
            label="Categoria"
            value={editCategory}
            onChange={e => setEditCategory(e.target.value)}
            fullWidth size="small" sx={{ mb: 2 }}
            helperText="Sovrascrive la categoria rilevata dal tag CATEGORY: della descrizione"
          >
            <MenuItem value="">Automatica (dal tag eBay)</MenuItem>
            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Visibilità sul sito"
            value={editVisible}
            onChange={e => setEditVisible(e.target.value)}
            fullWidth size="small"
            helperText="Forza la visibilità a prescindere da categoria e account"
          >
            <MenuItem value="auto">Automatica (regola per-account)</MenuItem>
            <MenuItem value="show">Mostra sempre</MenuItem>
            <MenuItem value="hide">Nascondi sempre</MenuItem>
          </TextField>

          {editPrice && editItem && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Risparmio: {fmt(editItem.originalPrice - parseFloat(editPrice || 0))}
              {' '}(−{((1 - parseFloat(editPrice) / editItem.originalPrice) * 100).toFixed(1)}%)
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Annulla</Button>
          <Button variant="contained" onClick={saveEdit} disabled={editSaving}
            startIcon={editSaving ? <CircularProgress size={16} /> : <SaveIcon />}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
